import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useResponseFormatting } from '../formatting/ResponseFormattingContext';

export const ChatSettingsPanel: React.FC = () => {
  const { settings, updateSetting, resetToDefaults } = useResponseFormatting();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Response Formatting</h3>
        
        {/* Code Block Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="syntax-highlighting" className="text-xs">
              Syntax Highlighting
            </Label>
            <Switch
              id="syntax-highlighting"
              checked={settings.syntaxHighlighting}
              onCheckedChange={(checked) => updateSetting('syntaxHighlighting', checked)}
              className="scale-75"
            />
          </div>

          {settings.syntaxHighlighting && (
            <div className="ml-4">
              <Label className="text-xs text-muted-foreground">Code Theme</Label>
              <Select
                value={settings.codeTheme}
                onValueChange={(value) => updateSetting('codeTheme', value as any)}
              >
                <SelectTrigger className="w-full mt-1 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="github">GitHub</SelectItem>
                  <SelectItem value="monokai">Monokai</SelectItem>
                  <SelectItem value="dracula">Dracula</SelectItem>
                  <SelectItem value="vs2015">VS2015</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Separator className="my-4" />

        {/* Math Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="math-rendering" className="text-xs">
              Math Rendering
            </Label>
            <Switch
              id="math-rendering"
              checked={settings.mathRendering}
              onCheckedChange={(checked) => updateSetting('mathRendering', checked)}
              className="scale-75"
            />
          </div>
        </div>

        <Separator className="my-4" />

        {/* Table Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-format-tables" className="text-xs">
              Auto-format Tables
            </Label>
            <Switch
              id="auto-format-tables"
              checked={settings.autoFormatTables}
              onCheckedChange={(checked) => updateSetting('autoFormatTables', checked)}
              className="scale-75"
            />
          </div>

          {settings.autoFormatTables && (
            <div className="ml-4">
              <Label className="text-xs text-muted-foreground">Table Style</Label>
              <Select
                value={settings.tableStyle}
                onValueChange={(value) => updateSetting('tableStyle', value as any)}
              >
                <SelectTrigger className="w-full mt-1 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="bordered">Bordered</SelectItem>
                  <SelectItem value="striped">Striped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Separator className="my-4" />

        {/* Link Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-detect-links" className="text-xs">
              Auto-detect Links
            </Label>
            <Switch
              id="auto-detect-links"
              checked={settings.autoDetectLinks}
              onCheckedChange={(checked) => updateSetting('autoDetectLinks', checked)}
              className="scale-75"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="open-links-new-tab" className="text-xs">
              Open Links in New Tab
            </Label>
            <Switch
              id="open-links-new-tab"
              checked={settings.openLinksInNewTab}
              onCheckedChange={(checked) => updateSetting('openLinksInNewTab', checked)}
              className="scale-75"
            />
          </div>
        </div>

        <Separator className="my-4" />

        {/* Message Display Settings */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Message Density</Label>
            <Select
              value={settings.messageDensity}
              onValueChange={(value) => updateSetting('messageDensity', value as any)}
            >
              <SelectTrigger className="w-full mt-1 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="compact">Compact</SelectItem>
                <SelectItem value="comfortable">Comfortable</SelectItem>
                <SelectItem value="spacious">Spacious</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Font Size</Label>
            <Select
              value={settings.fontSize}
              onValueChange={(value) => updateSetting('fontSize', value as any)}
            >
              <SelectTrigger className="w-full mt-1 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Separator />

      <div className="pt-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={resetToDefaults}
          className="w-full"
        >
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};